import { STAFF_CATALOG_ITEM_SUBPATHS, STAFF_CATALOG_PATHS, STAFF_CATALOG_PLACE_REVIEW_PATH, STAFF_CATALOG_PLACE_SUBPATHS, STAFF_CATALOG_SEARCH_PATH, staffCatalogCategoryListSchema, staffCatalogHistorySchema, staffCatalogItemDetailSchema, staffCatalogItemPageSchema, staffCatalogItemPath, staffCatalogLocationResultSchema, staffCatalogNotesSchema, staffCatalogPersonPageSchema, staffCatalogPlacePath, staffCatalogPlaceReviewPageSchema, staffCatalogPlaceSearchResultSchema, staffCatalogPlaceVerificationQueuedSchema, staffCatalogPlaceViewSchema, staffCatalogSearchResultSchema, staffCatalogTagListSchema, staffCatalogUploadTargetSchema, staffCommandOutcomeSchema, } from "@lafayette/schemas";
import { ApiHttpError, ApiNetworkError, ApiResponseError } from "./api-failure";
import { createJsonTransport } from "./json-transport";
// --- errors -----------------------------------------------------------------------------------------
/**
 * The API answered with an error. `code` is the envelope's code (`conflict` for 409, `unprocessable`
 * for 422, `forbidden` for 403, `service_unavailable` for 503, ...). The envelope has no rule code by
 * design: on a `conflict` the UI reads the item again and compares `version` to tell "changed by
 * someone else" from "see the readiness issues".
 */
export class StaffCatalogApiError extends ApiHttpError {
    constructor(status, code, message, requestId) {
        super(status, code, message, requestId);
        this.name = "StaffCatalogApiError";
    }
}
/** The request never got an answer (no network, DNS, a closed connection) after every retry. */
export class StaffCatalogNetworkError extends ApiNetworkError {
    constructor(options) {
        super("the catalog API could not be reached", options);
        this.name = "StaffCatalogNetworkError";
    }
}
/** The API answered 2xx with a body that does not match the contract. */
export class StaffCatalogResponseError extends ApiResponseError {
    constructor() {
        super("the catalog API answered with an unexpected body");
        this.name = "StaffCatalogResponseError";
    }
}
/** Builds the query string. A key with no value is left out. An array repeats the key. The order is stable. */
export function staffCatalogSearchParams(query) {
    const params = new URLSearchParams();
    for (const key of Object.keys(query).sort()) {
        const value = query[key];
        if (value === undefined)
            continue;
        if (typeof value === "object") {
            for (const entry of value)
                params.append(key, entry);
        }
        else {
            params.append(key, String(value));
        }
    }
    return params;
}
export function createStaffCatalogApi(options) {
    const send = createJsonTransport(options, {
        refusedMessage: "the catalog API refused the request",
        network: (cause) => new StaffCatalogNetworkError({ cause }),
        response: () => new StaffCatalogResponseError(),
        http: ({ status, code, message, requestId }) => new StaffCatalogApiError(status, code, message, requestId),
    });
    const read = (path, schema, query, callOptions) => send({
        method: "GET",
        path,
        schema,
        ...(query === undefined ? {} : { query: staffCatalogSearchParams(query) }),
        signal: callOptions?.signal,
    });
    const command = (path, schema, body, commandOptions, maxNetworkRetries) => send({
        method: "POST",
        path,
        schema,
        body,
        ...(commandOptions?.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: commandOptions.idempotencyKey }),
        ...(maxNetworkRetries === undefined ? {} : { maxNetworkRetries }),
        signal: commandOptions?.signal,
    });
    const itemCommand = (itemId, subpath, body, commandOptions) => command(staffCatalogItemPath(itemId, subpath), staffCatalogItemDetailSchema, body, commandOptions);
    const sub = STAFF_CATALOG_ITEM_SUBPATHS;
    const places = STAFF_CATALOG_PLACE_SUBPATHS;
    return {
        listItems: (query, o) => read(STAFF_CATALOG_PATHS.items, staffCatalogItemPageSchema, query, o),
        getItem: (itemId, o) => read(staffCatalogItemPath(itemId), staffCatalogItemDetailSchema, undefined, o),
        listNotes: (itemId, o) => read(staffCatalogItemPath(itemId, sub.internalNotes), staffCatalogNotesSchema, undefined, o),
        listHistory: (itemId, query, o) => read(staffCatalogItemPath(itemId, sub.history), staffCatalogHistorySchema, query, o),
        searchCategories: (query, o) => read(STAFF_CATALOG_PATHS.categories, staffCatalogCategoryListSchema, query, o),
        searchTags: (query, o) => read(STAFF_CATALOG_PATHS.tags, staffCatalogTagListSchema, query, o),
        searchPeople: (query, o) => read(STAFF_CATALOG_PATHS.people, staffCatalogPersonPageSchema, query, o),
        createItem: (input, o) => command(STAFF_CATALOG_PATHS.items, staffCatalogItemDetailSchema, input, o),
        updateDetails: (itemId, input, o) => itemCommand(itemId, sub.details, input, o),
        updateSubtype: (itemId, input, o) => itemCommand(itemId, sub.subtype, input, o),
        updatePricing: (itemId, input, o) => itemCommand(itemId, sub.pricing, input, o),
        authorizePrice: (itemId, input, o) => itemCommand(itemId, sub.priceAuthorization, input, o),
        replaceCategories: (itemId, input, o) => itemCommand(itemId, sub.categories, input, o),
        replaceTags: (itemId, input, o) => itemCommand(itemId, sub.tags, input, o),
        replacePeople: (itemId, input, o) => itemCommand(itemId, sub.people, input, o),
        replaceMedia: (itemId, input, o) => itemCommand(itemId, sub.media, input, o),
        createMediaUploadTarget: (itemId, input, o) => command(staffCatalogItemPath(itemId, sub.mediaUploads), staffCatalogUploadTargetSchema, input, o),
        addExternalRef: (itemId, input, o) => itemCommand(itemId, sub.externalRefs, input, o),
        markVerified: (itemId, input, o) => itemCommand(itemId, sub.verify, input, o),
        addNote: (itemId, input, o) => command(staffCatalogItemPath(itemId, sub.internalNotes), staffCatalogNotesSchema, input, o),
        submitForReview: (itemId, input, o) => itemCommand(itemId, sub.submitForReview, input, o),
        returnToDraft: (itemId, input, o) => itemCommand(itemId, sub.returnToDraft, input, o),
        publish: (itemId, input, o) => itemCommand(itemId, sub.publish, input, o),
        unpublish: (itemId, input, o) => itemCommand(itemId, sub.unpublish, input, o),
        archive: (itemId, input, o) => itemCommand(itemId, sub.archive, input, o),
        createCategory: (input, o) => command(STAFF_CATALOG_PATHS.categories, staffCommandOutcomeSchema, input, o),
        createTag: (input, o) => command(STAFF_CATALOG_PATHS.tags, staffCommandOutcomeSchema, input, o),
        createPerson: (input, o) => command(STAFF_CATALOG_PATHS.people, staffCommandOutcomeSchema, input, o),
        getPlace: (itemId, o) => read(staffCatalogPlacePath(itemId, places.place), staffCatalogPlaceViewSchema, undefined, o),
        searchPlace: (itemId, input, o) => command(staffCatalogPlacePath(itemId, places.search), staffCatalogPlaceSearchResultSchema, input, o, 0),
        selectPlace: (itemId, input, o) => command(staffCatalogPlacePath(itemId, places.select), staffCatalogPlaceViewSchema, input, o),
        clearPlace: (itemId, input, o) => command(staffCatalogPlacePath(itemId, places.clear), staffCatalogPlaceViewSchema, input, o),
        requestPlaceVerification: (itemId, input, o) => command(staffCatalogPlacePath(itemId, places.verify), staffCatalogPlaceVerificationQueuedSchema, input, o),
        confirmLocation: (itemId, input, o) => command(staffCatalogPlacePath(itemId, places.location), staffCatalogLocationResultSchema, input, o),
        listPlaceReview: (query, o) => read(STAFF_CATALOG_PLACE_REVIEW_PATH, staffCatalogPlaceReviewPageSchema, query, o),
        searchCatalog: (input, o) => command(STAFF_CATALOG_SEARCH_PATH, staffCatalogSearchResultSchema, input, o),
    };
}
